import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { playSequence, EASE } from "./lib/anim";
import { Icon } from "./components/Icons";
import { STUDIO, useStore } from "./lib/store";
import { usePmStore } from "./lib/pmStore";
import { Avatar, Modal, Plate } from "./components/ui";
import { SyncBadge } from "./components/SyncBadge";
import { RequestAccessModal } from "./RequestAccess";
import { useAccessRequests } from "./lib/accessStore";
import { useSettings, settingsActions, passphraseActive, sha256Hex } from "./lib/settingsStore";
import { serverSetPassphrase } from "./lib/managerAuth";
import { getSyncStatus } from "./lib/sync";
import { homeFor, loginWithCode, useSession, logout, canOpenBid, canOpenPm, pmFullAccess, Session } from "./lib/auth";

/** Said after "that module isn't yours" — points at the person who can change it. */
const DENIED = "Ask the studio to change your role if you need it.";

/* One-time preload: a dark drafting-sheet curtain — title block rules draw in,
   the wordmark settles, then the whole sheet lifts away like a cover page.
   Once per browser session; skipped under prefers-reduced-motion. */
function Preload() {
  const [gone, setGone] = useState(() =>
    sessionStorage.getItem("atelier.preloaded") === "1" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (gone) return;
    const t1 = setTimeout(() => setLeaving(true), 1650);
    const t2 = setTimeout(() => { sessionStorage.setItem("atelier.preloaded", "1"); setGone(true); }, 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [gone]);

  if (gone) return null;
  const mark = (pos: React.CSSProperties, delay: string) => (
    <div style={{ position: "absolute", width: 22, height: 22, opacity: 0, animation: `pjFade .5s ${delay} ease both`, ...pos }}>
      <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 1, background: "rgba(246,242,232,.35)" }} />
      <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "rgba(246,242,232,.35)" }} />
    </div>
  );
  return (
    <div aria-hidden style={{
      position: "fixed", inset: 0, zIndex: 300, background: "#26241f", color: "#f6f2e8",
      display: "grid", placeItems: "center", overflow: "hidden",
      transform: leaving ? "translateY(-100%)" : "none",
      transition: "transform .85s cubic-bezier(.76,0,.24,1)",
      pointerEvents: leaving ? "none" : "auto",
    }}>
      {/* sheet frame */}
      <div style={{ position: "absolute", inset: 18, border: "1px solid rgba(246,242,232,.16)", borderRadius: 4, animation: "pjFade .6s .1s ease both", opacity: 0 }} />
      {/* registration marks */}
      {mark({ top: 34, left: 34 }, ".55s")}
      {mark({ top: 34, right: 34 }, ".65s")}
      {mark({ bottom: 34, left: 34 }, ".75s")}
      {mark({ bottom: 34, right: 34 }, ".85s")}

      <div style={{ textAlign: "center", padding: "0 24px", transform: leaving ? "translateY(26px)" : "none", transition: "transform .85s cubic-bezier(.76,0,.24,1)" }}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: 6, opacity: 0, animation: "pjFade .6s .25s ease both", color: "rgba(246,242,232,.55)" }}>
          SHT 01 · COVER
        </div>
        {/* rule draws outward from centre */}
        <div style={{ height: 1, background: "rgba(246,242,232,.4)", margin: "16px auto 0", width: "min(360px, 70vw)", transformOrigin: "center", animation: "pjRule .9s .2s cubic-bezier(.2,.9,.2,1) both" }} />
        <div style={{ overflow: "hidden", marginTop: 14 }}>
          <div className="serif" style={{ fontSize: "clamp(30px,5.2vw,46px)", lineHeight: 1.08, animation: "pjSettle .9s .4s cubic-bezier(.2,.9,.2,1) both" }}>
            Atelier Project Studio
          </div>
        </div>
        <div style={{ height: 1, background: "rgba(246,242,232,.4)", margin: "14px auto 0", width: "min(360px, 70vw)", transformOrigin: "center", animation: "pjRule .9s .5s cubic-bezier(.2,.9,.2,1) both" }} />
        <div className="kicker" style={{ fontSize: 9.5, marginTop: 16, color: "rgba(246,242,232,.55)", opacity: 0, animation: "pjFade .7s .8s ease both" }}>
          Bid & Construction Management
        </div>
      </div>

      <style>{`
        @keyframes pjRule { from { transform: scaleX(0) } to { transform: scaleX(1) } }
        @keyframes pjSettle { from { transform: translateY(115%); } to { transform: translateY(0) } }
        @keyframes pjFade { from { opacity: 0 } to { opacity: 1 } }
      `}</style>
    </div>
  );
}

export default function Welcome() {
  const nav = useNavigate();
  const { tenders } = useStore();
  const { projects } = usePmStore();
  const session = useSession();
  const root = useRef<HTMLDivElement>(null);
  const [code, setCode] = useState(() => new URLSearchParams(window.location.search).get("code") ?? "");
  const [error, setError] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [cockpit, setCockpit] = useState(false);
  const pendingAccess = useAccessRequests().filter((r) => r.status === "pending").length;
  const hasPassphrase = passphraseActive(useSettings());

  useEffect(() => {
    return playSequence(root.current, [
      { sel: ".w-kick", y: 20, duration: 0.7 },
      { sel: ".w-line", y: "120%", duration: 1, stagger: 0.12, offset: -0.4 },
      { sel: ".w-sub", y: 16, duration: 0.7, offset: -0.5 },
      { sel: ".w-card", y: 30, duration: 0.7, stagger: 0.1, offset: -0.4 },
      { sel: ".w-foot", duration: 0.6, offset: -0.3 },
    ], EASE.power4);
  }, []);

  const open = tenders.filter((t) => t.status === "open").length;

  async function submit() {
    setError("");
    const s = await loginWithCode(code);
    if (!s) { setError("That code doesn't match a room. Check the invitation email."); return; }
    if (s.role !== "manager") nav(homeFor(s));
    setCode("");
  }

  /* Open the module the card names. This used to send every non-manager to
     homeFor(session) regardless of which card they clicked, so both cards led
     to the same screen for teammates. Now the module decides, and a teammate
     who has no seat in that module is told so rather than silently redirected. */
  function enterModule(module: "bid" | "pm", s: Session | null) {
    if (!s) { setError("Enter your access code first — the modules unlock per role."); return; }
    setError("");
    if (s.role === "manager") { nav(module === "bid" ? "/manage" : "/pm"); return; }

    if (module === "bid") {
      if (!canOpenBid(s)) { setError(`The Bid Manager isn't part of your access. ${DENIED}`); return; }
      // a tender-scoped member lands in their own room; studio-wide roles get the list
      nav(s.role === "bidteam" && s.tenderId ? `/manage/${s.tenderId}/dashboard` : "/manage");
      return;
    }

    if (!canOpenPm(s)) { setError(`The Project Manager isn't part of your access. ${DENIED}`); return; }
    if (pmFullAccess(s)) { nav("/pm"); return; }
    if (s.role === "pclient") { nav(`/site/${s.projectId}`); return; }
    nav("/pm/select");  // one login → pick the job (forwards straight in if only one)
  }

  return (
    <div ref={root} style={{ minHeight: "100vh", background: "var(--paper)", display: "flex", flexDirection: "column", padding: "clamp(24px, 6vw, 72px)", position: "relative", overflow: "hidden" }}>
      <Preload />
      {/* ambient plate */}
      <div style={{ position: "absolute", top: "-10%", right: "-8%", width: 520, height: 520, opacity: 0.5, filter: "blur(4px)", pointerEvents: "none" }}>
        <div style={{ borderRadius: "50%", overflow: "hidden", width: "100%", height: "100%" }}><Plate hue={24} h={520} seed={4} /></div>
      </div>

      <header className="row between" style={{ zIndex: 1 }}>
        <div className="row" style={{ gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "var(--ink)", color: "var(--paper)", display: "grid", placeItems: "center" }}><Icon.logo width={22} /></div>
          <div><div className="serif" style={{ fontSize: 20, lineHeight: 1 }}>{STUDIO.name}</div><div className="kicker" style={{ fontSize: 9.5, marginTop: 3 }}>{STUDIO.unit}</div></div>
        </div>
        <span className="row" style={{ gap: 12 }}>
          <span className="row" style={{ gap: 8 }}>
            <span className="live-dot" />
            {/* Signed out, the store only holds the built-in samples — quoting
                them as live work misled anyone arriving from another app's
                browser. Say what to do instead. */}
            <span className="dim" style={{ fontSize: 12.5 }}>
              {session
                ? <>{open} tender{open === 1 ? "" : "s"} in bidding · {projects.length} project{projects.length === 1 ? "" : "s"} on site</>
                : <>Sign in to load your tenders and projects</>}
            </span>
          </span>
          <SyncBadge />
          {session ? (
            <span className="row" style={{ gap: 8, padding: "4px 6px 4px 5px", border: "1px solid var(--line)", borderRadius: 40, background: "var(--card)" }}>
              <Avatar name={"company" in session ? session.company : STUDIO.manager} hue={session.role === "manager" ? 20 : 200} size={26} />
              <span style={{ fontSize: 12.5, fontWeight: 500 }}>{session.name}</span>
              {session.role === "manager" && (
                <>
                  {pendingAccess > 0 && (
                    <button className="btn accent sm" onClick={() => nav("/access")} style={{ padding: "3px 10px", fontSize: 11 }} title="Access requests">
                      <Icon.key width={11} />{pendingAccess}
                    </button>
                  )}
                  <button className="iconbtn" onClick={() => nav("/account")} style={{ width: 26, height: 26 }} aria-label="My cockpit" title="My cockpit"><Icon.lock width={12} /></button>
                </>
              )}
              <button className="iconbtn" onClick={() => logout()} style={{ width: 26, height: 26 }} aria-label="Sign out" title="Sign out"><Icon.logout width={12} /></button>
            </span>
          ) : (
            <button className={"btn sm " + (hasPassphrase ? "soft" : "accent")} onClick={() => setCockpit(true)}>
              <Icon.lock width={14} />
              {hasPassphrase ? "Manager sign in" : "Set up my cockpit"}
            </button>
          )}
        </span>
      </header>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", maxWidth: 1160, zIndex: 1 }}>
        <div className="w-kick kicker" style={{ marginBottom: 18 }}>Construction delivery suite</div>
        <h1 className="serif" style={{ fontSize: "clamp(42px, 8vw, 98px)", lineHeight: 0.95, letterSpacing: "-0.03em" }}>
          <div style={{ overflow: "hidden" }}><div className="w-line">One studio,</div></div>
          <div style={{ overflow: "hidden" }}><div className="w-line" style={{ fontStyle: "italic", color: "var(--accent-ink)" }}>bid to built.</div></div>
        </h1>
        <p className="w-sub muted" style={{ fontSize: "clamp(15px, 2vw, 18.5px)", maxWidth: 620, marginTop: 24 }}>
          Run the tender in the Bid Manager, then carry the winner straight onto site — drawings with
          living revisions, RFIs, daily logs, the work programme and material requests, all in one calm room.
        </p>

        {/* A signed-in teammate who picks a module they don't hold needs to be
            told so here — the sign-in card below carries the same message, but
            it is gone once they are in. */}
        {session && error && (
          <p className="w-sub" style={{ color: "var(--danger)", fontSize: 13, marginTop: 22, maxWidth: 620 }}>{error}</p>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18, marginTop: 42, maxWidth: 1100 }}>
          {/* access — only while signed out; once in, the header chip carries identity */}
          {!session && (
            <div className="w-card card" style={{ padding: 24 }}>
              <Icon.key width={24} style={{ color: "var(--accent-ink)" }} />
              <div className="serif" style={{ fontSize: 24, marginTop: 16 }}>Enter your room</div>
              <p className="muted" style={{ marginTop: 8, fontSize: 13.5 }}>Managers, clients, bidders and contractors each sign in with their access code.</p>
              <div className="row" style={{ gap: 8, marginTop: 16 }}>
                <input
                  className="input mono" autoFocus placeholder="Access code or passphrase"
                  value={code} style={{ letterSpacing: ".06em" }}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && void submit()}
                />
                <button className="btn accent" onClick={() => void submit()}><Icon.arrow width={16} /></button>
              </div>
              <p className="dim" style={{ fontSize: 11, marginTop: 12 }}>
                {hasPassphrase
                  ? <>Demo — client <b className="mono">VETTER</b> · bidder <b className="mono">BT-CORNER</b> · contractor <b className="mono">PG-CORNER</b> · manager uses a private passphrase</>
                  : <>Demo — manager <b className="mono">ATELIER</b> · client <b className="mono">VETTER</b> · bidder <b className="mono">BT-CORNER</b> · contractor <b className="mono">PG-CORNER</b></>}
              </p>
              <button className="btn ghost sm" style={{ marginTop: 12 }} onClick={() => setRequesting(true)}>
                <Icon.key width={13} />No code yet? Request access
              </button>
              {error && <p style={{ color: "var(--danger)", fontSize: 12.5, marginTop: 10 }}>{error}</p>}
            </div>
          )}

          {/* bid manager */}
          <button className="w-card card hoverable" onClick={() => enterModule("bid", session)} style={{ textAlign: "left", padding: 24, cursor: "pointer", background: "var(--ink)", color: "var(--paper)", border: "none" }}>
            <Icon.gavel width={24} style={{ color: "var(--accent)" }} />
            <div className="serif" style={{ fontSize: 24, marginTop: 16 }}>Bid Manager</div>
            <p style={{ opacity: 0.7, marginTop: 8, fontSize: 13.5 }}>
              Tenders end to end — documents & addenda, RFIs, sealed bids on a live countdown, results and award.
            </p>
            <div className="row" style={{ gap: 6, marginTop: 16, fontSize: 13, color: "var(--accent)" }}>{session ? `${open} open for bids` : "Sign in to open"} <Icon.arrow width={15} /></div>
          </button>

          {/* project manager */}
          <button className="w-card card hoverable" onClick={() => enterModule("pm", session)} style={{ textAlign: "left", padding: 24, cursor: "pointer" }}>
            <Icon.program width={24} style={{ color: "var(--accent-ink)" }} />
            <div className="serif" style={{ fontSize: 24, marginTop: 16 }}>Project Manager</div>
            <p className="muted" style={{ marginTop: 8, fontSize: 13.5 }}>
              Day-to-day on site — drawings with auto-revisions, daily logs & targets, the Gantt programme, materials, punch list and reports.
            </p>
            <div className="row" style={{ gap: 6, marginTop: 16, fontSize: 13, color: "var(--accent-ink)" }}>{session ? `${projects.length} live project${projects.length === 1 ? "" : "s"}` : "Sign in to open"} <Icon.arrowUpRight width={15} /></div>

          </button>

          {/* cross-link to the Atelier design studio + client portal */}
          <a className="w-card card hoverable" href="https://studiogy.design" style={{ textAlign: "left", padding: 24, textDecoration: "none", color: "inherit" }}>
            <Icon.logo width={24} style={{ color: "var(--accent-ink)" }} />
            <div className="serif" style={{ fontSize: 24, marginTop: 16 }}>Atelier — design studio</div>
            <p className="muted" style={{ marginTop: 8, fontSize: 13.5 }}>
              The design-side client portal: brief, presentation, design stages, schedule, program, feedback and quotes. Switch across when the work is on the drawing board.
            </p>
            <div className="row" style={{ gap: 6, marginTop: 16, fontSize: 13, color: "var(--accent-ink)" }}>Open studiogy.design <Icon.arrowUpRight width={15} /></div>
          </a>
        </div>
      </div>

      <RequestAccessModal open={requesting} onClose={() => setRequesting(false)} />
      <CockpitModal open={cockpit} onClose={() => setCockpit(false)} />

      <footer className="w-foot row between wrap" style={{ zIndex: 1, gap: 14, marginTop: 30 }}>
        <span className="dim" style={{ fontSize: 12.5, fontStyle: "italic" }}>“{STUDIO.tagline}”</span>
        <span className="dim" style={{ fontSize: 12 }}>{STUDIO.manager}, Studio manager · draft contracts via Contract Generator</span>
      </footer>
    </div>
  );
}

/* --- manager cockpit: first click creates the account, after that it logs in */
function CockpitModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const hasPassphrase = passphraseActive(useSettings());
  const [pass, setPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  function reset() { setPass(""); setConfirm(""); setError(""); }

  /* one-time setup: set the passphrase server-side, sign in, land in the
     cockpit. The server refuses if a passphrase already exists (a device that
     hasn't synced yet can't be used to overwrite the real one). */
  async function create() {
    setError("");
    if (pass.length < 8) { setError("Use at least 8 characters — a short sentence works well."); return; }
    if (pass !== confirm) { setError("The two entries don't match."); return; }
    setBusy(true);
    if (getSyncStatus() === "local") {
      // plain local dev — no worker to hold the credential
      settingsActions.setPassphraseHash(await sha256Hex(pass.trim()));
    } else {
      const r = await serverSetPassphrase(pass.trim());
      if (!r.ok) {
        setBusy(false);
        setError(r.reason === "unauthorized"
          ? "A passphrase is already set for this studio — use Manager sign in with it instead."
          : "The server can't be reached right now — try again in a moment.");
        return;
      }
      settingsActions.markPassphraseSet(true);
    }
    await loginWithCode(pass.trim());
    setBusy(false); reset();
    onClose();
    nav("/account");
  }

  async function enter() {
    setError(""); setBusy(true);
    const s = await loginWithCode(pass);
    setBusy(false);
    if (!s || s.role !== "manager") { setError("That passphrase doesn't match. It's case-sensitive."); return; }
    reset();
    onClose();
  }

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title={hasPassphrase ? "Manager sign in" : "Set up your cockpit"}>
      <div className="col" style={{ gap: 14 }}>
        <div className="row" style={{ gap: 12 }}>
          <Avatar name={STUDIO.manager} hue={20} size={42} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14.5 }}>{STUDIO.manager}</div>
            <div className="dim" style={{ fontSize: 12 }}>{STUDIO.managerEmail} · full access to both modules</div>
          </div>
        </div>

        {hasPassphrase ? (
          <>
            <div className="field">
              <label>Your passphrase</label>
              <input className="input" type="password" autoFocus value={pass} onChange={(e) => setPass(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void enter()} autoComplete="current-password" />
            </div>
            {error && <p style={{ color: "var(--danger)", fontSize: 13 }}>{error}</p>}
            <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
              <button className="btn ghost" onClick={() => { reset(); onClose(); }}>Cancel</button>
              <button className="btn accent" onClick={() => void enter()} disabled={busy || !pass}><Icon.unlock width={15} />{busy ? "…" : "Enter cockpit"}</button>
            </div>
          </>
        ) : (
          <>
            <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
              One-time setup: choose a personal passphrase and this becomes your account. From then on this button signs
              you in with it, and the shared <b className="mono">ATELIER</b> demo code stops working. It applies on every
              device instantly, and only its fingerprint is stored — never the passphrase itself.
            </p>
            <div className="field">
              <label>Choose a passphrase — case-sensitive, 8+ characters</label>
              <input className="input" type="password" autoFocus value={pass} onChange={(e) => setPass(e.target.value)}
                placeholder="e.g. bartica-ring-beam-31" autoComplete="new-password" />
            </div>
            <div className="field">
              <label>Confirm it</label>
              <input className="input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void create()} autoComplete="new-password" />
            </div>
            {error && <p style={{ color: "var(--danger)", fontSize: 13 }}>{error}</p>}
            <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
              <button className="btn ghost" onClick={() => { reset(); onClose(); }}>Cancel</button>
              <button className="btn accent" onClick={() => void create()} disabled={busy || pass.length < 8 || pass !== confirm}>
                <Icon.key width={15} />{busy ? "…" : "Set passphrase & enter"}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
